import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useTrackOnce } from '#src/app/shared/hooks/tracking';

import { usePromoteFormData, usePromoteFormState } from '../../../../../../formContext';
import { useTracking } from '../../../../../../shared/hooks/useTracking';

import { useButtonAmounts } from './hooks/useButtonAmounts';
import { useCustomAmount } from './hooks/useCustomAmount';

type Result = ModelHookResult<
	{
		buttonAmounts: Array<ReturnType<typeof useButtonAmounts>[number]>;
		selection: string | undefined;
		customAmount: string;
		amountErrorText: string | null;
		customAmountLabel: string;
		currencySymbol: string;
		currencySymbolPosition: 'before' | 'after';
	},
	{
		handleAmountChange: (value: string) => void;
		handleCustomAmountFocus: () => void;
		handleCustomAmountChange: (value: ChangeEvent<HTMLInputElement>) => void;
	}
>;

export function useAmountSelector(): Result {
	const {
		amountChoices,
		currency: { code: currencyCode },
	} = usePromoteFormData();
	const [
		{ amount: amountInPageState, validationErrors, promoteFormRevealed },
		{ setAmount, setCustomAmountSelected, setValidationErrors },
	] = usePromoteFormState();
	const { translate, getCurrencySymbol, getCurrencySymbolPosition } = useI18n();
	const { trackAmountChanged } = useTracking();
	const trackOnce = useTrackOnce();
	const [selection, setSelection] = useState<string | undefined>(() => {
		if (!amountInPageState) return undefined;
		if (amountChoices.some((choice) => choice.amount === amountInPageState)) {
			return `${amountInPageState}`;
		}
		return 'custom';
	});

	const buttonAmounts = useButtonAmounts();
	const customAmount = useCustomAmount();

	// This page's PromoteForm (and thus this hook) only renders for variant_1 — control and
	// fake_control navigate to the `promote` page instead, where amount always starts at 0 and
	// a manual selection is required. variant_1 instead preselect the first amount at init (see
	// useStateInitData), so fire the same "amount changed" event once for that pre-selection to give the
	// funnel a comparable intent step even when the user never touches a chip.
	useEffect(() => {
		if (!promoteFormRevealed) return;
		void trackOnce('pp_promotion_amount_changed', { button_click: false, button_clicked: 'p1' });
	}, [trackOnce, promoteFormRevealed]);

	const handleAmountChange = useCallback(
		(value: string): void => {
			setSelection(value);
			setCustomAmountSelected(value === 'custom');
			if (value !== 'custom') {
				const valueAsNumber = Number(value);
				const buttonIndex = amountChoices.findIndex((choice) => choice.amount === valueAsNumber) + 1;
				setValidationErrors({ ...validationErrors, amountSelection: '' });
				setAmount(valueAsNumber);
				trackAmountChanged({ buttonWasClicked: true, buttonClicked: `p${buttonIndex}` });
			} else {
				customAmount.actions.handleCustomAmountSelect();
			}
		},
		[
			amountChoices,
			setAmount,
			setCustomAmountSelected,
			setValidationErrors,
			trackAmountChanged,
			customAmount.actions,
			validationErrors,
		],
	);

	return {
		data: {
			buttonAmounts,
			selection,
			...customAmount.data,
			amountErrorText: validationErrors ? validationErrors.customAmount : null,
			customAmountLabel: translate('fe.pages.sponsors.other_amount'),
			currencySymbol: getCurrencySymbol(currencyCode),
			currencySymbolPosition: getCurrencySymbolPosition(currencyCode),
		},
		actions: {
			handleAmountChange,
			handleCustomAmountFocus: customAmount.actions.handleCustomAmountFocus,
			handleCustomAmountChange: customAmount.actions.handleCustomAmountChange,
		},
	};
}
